// src/oura/components/RemediationSection.tsx
//
// "Generate a fix" inside a drawer — the working half of ourteam-backend#3804,
// scoped to one tracker task or one audit issue.
//
// This lives INSIDE the existing drawer (IssueFixDrawer) rather
// than on a surface of its own. Drafts hang off a tracker task (#3801 B2
// confirmed Actions and Tracker tasks are one concept, so there is no separate
// actions entity), and an audit issue is what generated that task — so the fix
// belongs where the problem is described, not in a tab someone has to know to
// visit.
//
// The one rule that shapes everything here: PUBLISHING WRITES TO THE CLIENT'S
// LIVE WEBSITE. It is the only action in Oura that mutates something outside
// our own database. So it is two-step, it names the page it will change, and it
// is never a side effect of opening this section.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
    CopyIcon,
    FileTextIcon,
    Loader2Icon,
    SparklesIcon,
    UploadCloudIcon,
    UndoIcon,
    CheckCircle2Icon,
    AlertTriangleIcon,
    ClipboardListIcon,
    ChevronDownIcon,
    ChevronUpIcon,
} from "lucide-react";
import { Button } from "../primitives/Button";
import { Chip } from "../primitives/Chip";
import { SourceChip } from "../primitives/SourceChip";
import type { Citation, KnowledgeReadReport } from "@service/aeo/types";
// The house-style dropdown, from the shared library rather than a local
// primitive: this is the same Select every other filter/select in the product
// uses, and duplicating it inside the Oura module would be the drift the house
// style exists to prevent.
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { cn } from "../lib/cn";
import { useSnackbar } from "@hook/use-snackbar";
import type { PublishRefusal } from "@service/aeo/deliveryTypes";
import { PublishRefusalNotice } from "../publish/PublishRefusalNotice";
import {
    createDraft,
    listDrafts,
    listPublications,
    publishDraft,
    revertPublication,
    type AeoDraft,
    type AeoPublication,
} from "@service/aeo";

/**
 * Every type the backend can generate (`DRAFT_TYPES` in aeo_remediation.py).
 *
 * `label` is the whole option text — short enough to scan. The `hint` used to
 * be concatenated onto it, which produced options reading "Page copy — New body
 * content — you paste this one yourself"; it now renders as helper text under
 * the control, for the selected type only.
 *
 * DRAFT-ONLY types (`page_copy`, `alt_text`) are generated for a human to paste
 * and are never written to a live page, which is what their hints have to keep
 * saying. `alt_text` is draft-only for a specific reason: writing an `<img>`
 * tag in place is a different guardrail question from appending a
 * marker-wrapped block, and nobody has answered it.
 */
export const DRAFT_TYPES: Array<{ key: string; label: string; hint: string }> = [
    { key: "meta_description", label: "Meta description", hint: "A summary for search and AI previews." },
    { key: "faq_block", label: "FAQ block", hint: "Questions and answers appended to the page." },
    { key: "schema_jsonld", label: "Structured data", hint: "Machine-readable labels for this page." },
    // #4530 §2: anchoring a phrase already in the body, not a rewrite. The
    // workshop could propose a link and had no way to apply one until this
    // became a writable type.
    { key: "internal_link", label: "Internal link", hint: "Turns a phrase already on the page into a link." },
    { key: "alt_text", label: "Image descriptions", hint: "Written for you to paste in yourself — Oura never publishes this one." },
    { key: "page_copy", label: "New body content", hint: "Written for you to paste in yourself — Oura never publishes this one." },
];

/**
 * The fallback map, for payloads that predate `check.draftTypes`.
 *
 * The backend now DECLARES what a draft can fix per check (#4529 §1), and this
 * list is no longer the authority — read `issue.draftTypes` and only fall back
 * here when the field is absent.
 *
 * Kept because an older stored assessment sends checks without the field, and
 * on those, showing a reasonable set beats showing nothing. Deliberately NOT
 * kept in sync by hand: it disagreed with the backend for months, which is how
 * Core Web Vitals came to offer an FAQ block.
 */
const TYPES_FOR_ISSUE: Record<string, string[]> = {
    missing_meta: ["meta_description"],
    dup_titles: ["meta_description"],
    no_schema: ["schema_jsonld"],
    thin_content: ["page_copy"],
    orphan_pages: ["internal_link"],
    missing_alt: ["alt_text"],
    canonicals: ["schema_jsonld"],
};

/**
 * What this drawer may offer for this finding.
 *
 * `declared` is the backend's answer and wins whenever it is present, INCLUDING
 * when it is empty — `[]` means no draft resolves this finding, so the control
 * disappears rather than offering something that cannot work. That is the whole
 * of #4529 §1: every issue used to offer the same four types whatever the issue
 * was, so Core Web Vitals offered to write an FAQ block and "Links to pages
 * that no longer exist" offered a meta description.
 *
 * `undefined` is the older-payload case and falls back to the map above. An
 * unmapped key there returns everything, because showing too much is
 * recoverable and showing nothing is a dead end — but only when we genuinely do
 * not know, never when the backend has told us the answer is none.
 */
export function typesForIssue(issueKey: string, declared?: string[]): typeof DRAFT_TYPES {
    const allowed = declared ?? TYPES_FOR_ISSUE[issueKey];
    if (allowed === undefined) return DRAFT_TYPES;
    return allowed
        .map((k) => DRAFT_TYPES.find((t) => t.key === k))
        .filter((t): t is (typeof DRAFT_TYPES)[number] => !!t);
}

function whenLabel(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Step-by-step instructions for a change we cannot write ourselves. */
function Guidance({ guidance }: { guidance: Record<string, unknown> }) {
    const steps = Array.isArray(guidance.steps) ? (guidance.steps as unknown[]) : [];
    if (steps.length === 0) return null;
    return (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                <ClipboardListIcon className="h-3.5 w-3.5" />
                Apply this by hand
            </p>
            <ol className="mt-1.5 space-y-1">
                {steps.map((step, i) => (
                    <li key={i} className="flex gap-2 text-xs leading-relaxed text-amber-900">
                        <span className="tabular-nums opacity-60">{i + 1}.</span>
                        <span>{String(step)}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

/**
 * The sources behind one draft (ourteam-backend#4836, K-1).
 *
 * Read defensively: `guidance` is `Record<string, unknown>` by design — every
 * draft type puts its own shape in there — so a missing or differently-shaped
 * `citations` must render nothing rather than throw. An absent array is the
 * normal case and means the draft has no document behind it, which is the
 * honest thing to show.
 */
function DraftCitations({ guidance }: { guidance: Record<string, unknown> }) {
    const raw = guidance?.citations;
    const citations = (Array.isArray(raw) ? raw : []).filter(
        (c): c is Citation =>
            !!c && typeof c === "object"
            && typeof (c as Citation).source_title === "string"
            && typeof (c as Citation).quote === "string",
    );
    if (citations.length === 0) return null;
    return (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-gray-400">Written from your documents:</span>
            <SourceChip citations={citations} />
        </div>
    );
}

/**
 * What the draft read from the client's documents, and what did not fit the
 * budget (#3756, `guidance_json.knowledge`). Quiet on purpose: most drafts read
 * nothing, and then this renders nothing. It speaks up only when a document was
 * left out, because a draft written without the price list is a draft a
 * reviewer should treat differently.
 */
function readKnowledgeReport(guidance: Record<string, unknown> | null | undefined): KnowledgeReadReport | null {
    const raw = guidance?.knowledge as Partial<KnowledgeReadReport> | undefined;
    if (!raw || typeof raw !== "object") return null;
    const used = Array.isArray(raw.used) ? raw.used.filter((d) => d && typeof d.title === "string") : [];
    const leftOut = Array.isArray(raw.leftOut) ? raw.leftOut.filter((d) => d && typeof d.title === "string") : [];
    if (used.length === 0 && leftOut.length === 0) return null;
    return {
        used,
        leftOut,
        budgetChars: typeof raw.budgetChars === "number" ? raw.budgetChars : 0,
        usedChars: typeof raw.usedChars === "number" ? raw.usedChars : 0,
    };
}

function DraftKnowledgeRead({ guidance }: { guidance: Record<string, unknown> }) {
    const report = readKnowledgeReport(guidance);
    if (!report) return null;
    const { used, leftOut } = report;
    const docs = (n: number) => `${n} ${n === 1 ? "document" : "documents"}`;
    return (
        <p className="mt-2 text-xs text-gray-400">
            Read {docs(used.length)} from the client
            {leftOut.length > 0 && (
                <span className="text-amber-700">
                    {" "}· {docs(leftOut.length)} did not fit and {leftOut.length === 1 ? "was" : "were"} not read:{" "}
                    {leftOut.map((d) => d.title).join(", ")}
                </span>
            )}
        </p>
    );
}

export function DraftRow({
    draft,
    onPublished,
    onMarkedByHand,
    showType,
    publishBlockedReason,
    publishUnsupported,
}: {
    draft: AeoDraft;
    /**
     * Called when a publish request came back accepted. `landed` is whether
     * the write to the client's site actually succeeded — the publication's
     * own status, not the request's. A caller that only wants to refetch can
     * ignore it; a caller that closes work off cannot (#3637).
     */
    onPublished: (landed: boolean) => void;
    /**
     * Record that the change was made by hand, on a page Oura cannot publish
     * to (#3639). Only offered where publishing is genuinely blocked — a page
     * Oura can write to should be published, not declared.
     *
     * Absent means no such route exists here (a fixture, or a caller with no
     * page status to set), and the control is not shown at all.
     */
    onMarkedByHand?: () => void | Promise<void>;
    /**
     * Name the draft type in the header.
     *
     * Off by default. On every issue with a single fix type the drawer header
     * already says it — a "Missing meta description" issue producing a card
     * labelled "Meta description" is the same word twice. It goes back on only
     * where the issue genuinely forks (thin content, orphan pages), because
     * there two cards under the same page differ by nothing else.
     */
    showType?: boolean;
    /**
     * Why publishing cannot work right now (#3400) — e.g. no CMS connector on
     * the business. Set = the Publish button renders disabled WITH the reason
     * beside it, instead of a live button whose click can only fail. Copy to
     * clipboard stays available either way: it is the fallback that needs no
     * connector.
     */
    publishBlockedReason?: string;
    /**
     * Why the connected platform cannot publish THIS draft type
     * (ourteam-backend#4761 / #3692), or undefined when it can.
     *
     * Separate from `publishBlockedReason`, which is about the business having
     * no platform at all. This one is about the platform having no route for
     * one particular type — a Wix site can publish a meta description and can
     * never publish an FAQ block. Absent resolver = the backend never said, so
     * nothing changes.
     */
    publishUnsupported?: (draftType: string) => string | undefined;
}) {
    const snackbar = useSnackbar();
    // Two-step by design: the first click arms, the second writes. This edits a
    // live customer website — one stray click must not do that.
    const [arming, setArming] = useState(false);
    const [publishing, setPublishing] = useState(false);
    /**
     * Fold the draft body away once it has been read.
     *
     * Expanded by default: on the first pass the reviewer has to see the copy
     * before they can trust it, and auto-collapsed cards would force a click on
     * every one. After that a long affected-pages list can turn into a scroll
     * of identical JSON blocks, so the header + a chevron is enough to skim by
     * page and expand only the ones being looked at.
     *
     * Session-scoped per card. A redraft mints a new draft id, so the fresh
     * copy comes back expanded rather than inheriting the previous decision.
     */
    const [collapsed, setCollapsed] = useState(false);
    /**
     * What the publish call itself said, held locally (#3516).
     *
     * The card used to derive its state purely from `draft.status`, which is a
     * RE-READ: after publishing, `onPublished()` refetches `listDrafts()`, and
     * if the BE has not flipped the draft row yet, the card renders un-published
     * and the Publish button comes back — on the one action in the product that
     * writes to a client's live website. Holding the returned publication means
     * the card stops depending on winning that race.
     */
    const [publishOutcome, setPublishOutcome] = useState<{ status: string; error: string | null } | null>(null);
    /** H20's gate refused the publish: the notice names the doc that needs the client's yes (2.23). */
    const [refusal, setRefusal] = useState<PublishRefusal | null>(null);
    const [markingByHand, setMarkingByHand] = useState(false);
    const published = draft.status === "published" || publishOutcome?.status === "published";
    /**
     * The platform's own answer about this type (#3692). Undefined = it can, or
     * nobody asked — either way the card behaves as it always has.
     *
     * Only asked for a draft that is publishable in principle. `alt_text` and
     * `page_copy` are paste-it-yourself on every platform, so blaming the
     * client's CMS for them — "WordPress has no way to publish image
     * descriptions" — would be both wrong and the kind of wrong someone acts
     * on, by going to look for a WordPress setting that does not exist.
     */
    const unsupportedReason = draft.publishable ? publishUnsupported?.(draft.draftType) : undefined;
    /**
     * Whether Publish belongs on this card at all.
     *
     * `draft.publishable` is about the TYPE in the abstract — alt text is
     * paste-it-yourself everywhere. This adds the platform: a meta description
     * is publishable in general and unpublishable on a site whose CMS has no
     * route for it. Both fold into one flag so every place that asked
     * "publishable?" keeps asking one question.
     */
    const publishableHere = draft.publishable && !unsupportedReason;
    // A 200 can carry `status: "failed"` or a still-queued publication. Neither
    // is "Published to the live site", and neither should silently re-offer the
    // button as though nothing was attempted.
    const publishFailed = !!publishOutcome && publishOutcome.status === "failed";
    const publishPending = !!publishOutcome
        && publishOutcome.status !== "published"
        && publishOutcome.status !== "failed";

    // The no-connector fallback (#3400): the drafted value goes onto the
    // clipboard for a hand-paste into whatever CMS the client actually has.
    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(draft.draft);
            snackbar.showSuccess("Copied to clipboard", "Paste it into the site's CMS.");
        } catch {
            snackbar.showError("Could not copy", "Select the draft text and copy it by hand.");
        }
    };

    const doPublish = async () => {
        setPublishing(true);
        setPublishOutcome(null);
        setRefusal(null);
        const result = await publishDraft(draft.id);
        setPublishing(false);
        setArming(false);
        if (result.ok) {
            // Believe the publication, not the HTTP status. `ok` only means the
            // request was accepted; the publication carries whether the write to
            // the client's site actually landed.
            const publication = result.publication;
            setPublishOutcome({ status: publication?.status ?? "", error: publication?.error ?? null });
            if (publication?.status === "published") {
                snackbar.showSuccess("Published to the live site");
            } else if (publication?.status === "failed") {
                snackbar.showError("Could not publish", publication.error || "The site rejected the change.");
            } else {
                snackbar.showSuccess(
                    "Publish queued",
                    "The change has been accepted and is still being written to the site.",
                );
            }
            onPublished(publication?.status === "published");
        } else if (result.refusal) {
            // Refused by the publish gate, not failed: the notice says why and what unlocks it.
            setRefusal(result.refusal);
        } else {
            // Record it, don't just announce it. A toast is gone in seconds and
            // this request takes ~9s against the client's WordPress, so by the
            // time the answer lands the operator has usually looked away — and
            // a card that has quietly gone back to offering the button is
            // exactly the "it did nothing" the D.1 report describes.
            //
            // `publishDraft` collapses every non-2xx into `ok: false`, so this
            // is the branch a 409 takes — which is the branch EVERY real
            // failure on this site takes (slug/permalink mismatch, plugin-owned
            // tag). Without this the "Publish failed" chip and the inline
            // reason below it could never render at all.
            setPublishOutcome({ status: "failed", error: result.message });
            // The BE's message verbatim — "WordPress rejected the credentials"
            // is what the user needs, not "something went wrong".
            snackbar.showError("Could not publish", result.message);
        }
    };

    const typeLabel = DRAFT_TYPES.find((t) => t.key === draft.draftType)?.label ?? draft.draftType;

    return (
        // Blue, and attributed. Every one of these is machine-written copy about
        // to be offered for a live site, and the card used to look exactly like
        // any other bordered panel in the drawer. The blue ground plus the
        // attribution line makes the provenance unmissable at a glance, which is
        // the one thing someone must not miss before publishing.
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3.5">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Same treatment as the OURA INSIGHTS eyebrow in
                            InsightsBanner — sparkle, 11px, uppercase, tracked,
                            blue-600. Both mark "a machine wrote this", so they
                            should read as the same voice rather than two
                            different-looking attributions. */}
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-600">
                            <SparklesIcon className="h-3.5 w-3.5" />
                            Drafted by Oura
                        </span>
                        {showType && <span className="text-sm text-gray-600">{typeLabel}</span>}
                        {/* "Ready to publish" is gone: the Publish button below says
                            the same thing and is the thing you act on. These two
                            survive because nothing else carries them — a published
                            draft has no button left, and a manual one never had one. */}
                        {published && (
                            <Chip className="border-emerald-200 bg-emerald-50 text-emerald-700">Published</Chip>
                        )}
                        {/* #3516: a 200 that did not publish must say so on the
                            card. Silently re-offering the button leaves the
                            operator unable to tell a retry from a duplicate
                            write to a live site. */}
                        {publishPending && (
                            <Chip className="border-blue-200 bg-blue-50 text-blue-700">Publishing…</Chip>
                        )}
                        {publishFailed && (
                            <Chip className="border-rose-200 bg-rose-50 text-rose-700">Publish failed</Chip>
                        )}
                        {!published && !draft.publishable && (
                            <Chip className="border-amber-200 bg-amber-50 text-amber-700">Manual</Chip>
                        )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-500" title={draft.pageUrl}>
                        {draft.pageUrl}
                    </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                    <span className="text-xs text-gray-400">{whenLabel(draft.createdAt)}</span>
                    {/* Copy lives in the card header, not the footer. It is a
                        utility on the content above it — always available, never
                        touching the live site — and sitting beside Publish made
                        two very different actions look like a pair. */}
                    <button
                        type="button"
                        onClick={() => void doCopy()}
                        aria-label="Copy this draft"
                        title="Copy to clipboard"
                        className="rounded-md p-1 text-gray-400 transition-colors hover:bg-white hover:text-gray-700"
                    >
                        <CopyIcon className="h-3.5 w-3.5" />
                    </button>
                    {/* Fold this card down to its header once it has been read.
                        Copy sits above the collapse control on purpose — copying
                        is what the reviewer does WITH the content, so it stays
                        available whether the JSON is visible or folded away. */}
                    <button
                        type="button"
                        onClick={() => setCollapsed((v) => !v)}
                        aria-expanded={!collapsed}
                        aria-label={collapsed ? "Show this draft" : "Hide this draft"}
                        title={collapsed ? "Show the draft" : "Hide the draft"}
                        className="rounded-md p-1 text-gray-400 transition-colors hover:bg-white hover:text-gray-700"
                    >
                        {collapsed ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronUpIcon className="h-3.5 w-3.5" />}
                    </button>
                </div>
            </div>

            {!collapsed && (
                <>
            {/* Monospaced: this is content that gets pasted or written verbatim,
                so it has to be readable character by character. White on the blue
                ground so the copy itself stays the brightest thing in the card. */}
            <pre className="mt-2.5 overflow-x-auto whitespace-pre-wrap rounded-md border border-blue-100 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-gray-800">
                {draft.draft}
            </pre>

            {draft.rationale && (
                <p className="mt-2 text-xs leading-relaxed text-gray-500">{draft.rationale}</p>
            )}

            {/* Layer 2 takes over wherever Layer 3 cannot reach — whether that is
                the type itself or this particular platform (#3692). */}
            {/* Where the draft's facts came from (#3756). On the DRAFT, not
                only inside the by-hand guidance: a publishable `faq_block` is
                exactly the case where a reviewer most needs to know a price came
                from the client's own price list. `guidance_json.citations` is
                where the backend puts them, beside the grounding note. */}
            <DraftCitations guidance={draft.guidance} />
            <DraftKnowledgeRead guidance={draft.guidance} />
            {!publishableHere && <Guidance guidance={draft.guidance} />}

            {refusal && !published && (
                <PublishRefusalNotice refusal={refusal} onRetry={() => void doPublish()} retrying={publishing} className="mt-3" />
            )}

            {/* Right-aligned. Publishing is the action this card exists to lead
                to; Copy is the always-available fallback (#3400) — it needs no
                connector and never touches the live site. */}
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                {arming ? (
                    <>
                        <span className="mr-auto text-xs text-gray-600">
                            This writes to the live site. Continue?
                        </span>
                        <Button size="sm" variant="ghost" disabled={publishing} onClick={() => setArming(false)}>
                            Cancel
                        </Button>
                        <Button size="sm" disabled={publishing} onClick={() => void doPublish()}>
                            {publishing ? (
                                <>
                                    <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                                    Publishing…
                                </>
                            ) : (
                                "Yes, publish"
                            )}
                        </Button>
                    </>
                ) : (
                    <>
                        {publishableHere && !published && publishBlockedReason && (
                            // Stated, not discovered-on-click: the button is
                            // disabled and the reason sits beside it.
                            <span className="mr-auto flex items-center gap-1.5 text-xs text-amber-700">
                                <AlertTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                {publishBlockedReason}
                            </span>
                        )}
                        {/* The way back out of a blocked page (#3639). Oura told
                            the operator to make the change in Elementor; until
                            now nothing let them say they had, so the finding sat
                            open over work that was finished. This is their word,
                            not a verified publish, and the label says so — the
                            next crawl is what settles it. */}
                        {publishableHere && !published && publishBlockedReason && onMarkedByHand && (
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={markingByHand}
                                onClick={() => {
                                    setMarkingByHand(true);
                                    void Promise.resolve(onMarkedByHand()).finally(() => setMarkingByHand(false));
                                }}
                            >
                                {markingByHand ? "Saving…" : "I made this change by hand"}
                            </Button>
                        )}
                        {publishFailed && publishOutcome?.error && (
                            <span className="mr-auto flex items-center gap-1.5 text-xs text-rose-700">
                                <AlertTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                {publishOutcome.error}
                            </span>
                        )}
                        {/* One line, naming the platform, where the button was.
                            "Publish" greyed out invites a click and a shrug; a
                            sentence saying Wix has no route for this type sends
                            the reviewer to the steps instead. */}
                        {unsupportedReason && !published && (
                            <span className="mr-auto flex items-center gap-1.5 text-xs text-amber-700">
                                <AlertTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                {unsupportedReason}
                            </span>
                        )}
                        {publishableHere && !published && !publishPending && (
                            <Button
                                size="sm"
                                disabled={!!publishBlockedReason}
                                title={publishBlockedReason}
                                onClick={() => setArming(true)}
                            >
                                <UploadCloudIcon className="h-3.5 w-3.5" />
                                Publish to site
                            </Button>
                        )}
                    </>
                )}
            </div>
                </>
            )}
        </div>
    );
}

/**
 * Every draft for a page, with superseded versions folded away.
 *
 * "Draft again" does not replace anything — the backend appends a row, so ten
 * redraws produced ten stacked cards and the drawer became a scroll of
 * near-identical paragraphs with no indication which one was current. True
 * overwrite is a backend decision (it owns the table); this is the display half,
 * and it is forward-compatible: once the backend supersedes on redraft each
 * group is one row and this collapses to a plain list.
 *
 * TWO drafts are always visible, never folded:
 *   - the newest, because that is what "Draft again" just produced;
 *   - anything PUBLISHED, because that one describes what is on the live site
 *     right now. Hiding it behind a toggle would hide the truth about the
 *     client's own website, which is the one thing this screen must not do.
 */
/** Scheme, `www.` and trailing slash removed — the same normalisation the
 *  audit joins pages on, and the BE's own `join_key`. */
function draftPageKey(url: string | undefined): string {
    return (url || "")
        .trim()
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "")
        .replace(/\/+$/, "")
        .toLowerCase();
}

export function DraftStack({
    drafts,
    onPublished,
    onMarkedByHand,
    showType,
    publishBlockedReason,
    publishUnsupported,
}: {
    drafts: AeoDraft[];
    onPublished: (landed: boolean) => void;
    /** See DraftRow — threaded through unchanged (#3639). */
    onMarkedByHand?: () => void | Promise<void>;
    showType?: boolean;
    /** See DraftRow — threaded through unchanged. */
    publishBlockedReason?: string;
    /** See DraftRow — threaded through unchanged (#3692). */
    publishUnsupported?: (draftType: string) => string | undefined;
}) {
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    // Keyed by page AND type: the same page can hold a body-copy draft and an
    // FAQ draft, and those supersede each other in separate lines.
    //
    // The page half is NORMALISED. Grouping on the raw `pageUrl` meant
    // "https://site.com/" and "https://site.com" were two different pages, so
    // two drafts of the same thing sat in two groups and each rendered as the
    // newest of its own — which is why one page could show several structured
    // data drafts from different dates while every other page showed one. The
    // homepage is the URL most likely to be spelled two ways, which is why it
    // was the one that looked broken.
    //
    // The BACKEND has the same join: `_supersede_previous` matches `page_url`
    // exactly, so those rows were never marked superseded server-side either.
    // This folds them in the UI; the backend ticket stops them being created
    // as separate live drafts at all.
    //
    // The separator was a NUL byte. It cannot collide, but it also makes the
    // whole file read as binary to grep — `grep "DraftStack"` finds nothing in
    // here, which costs more than the collision it prevents. A normalised URL
    // has no spaces in it, so a space separates just as safely.
    const groups = useMemo(() => {
        const byKey = new Map<string, AeoDraft[]>();
        for (const d of drafts) {
            const key = `${draftPageKey(d.pageUrl)} ${d.draftType}`;
            const list = byKey.get(key);
            if (list) list.push(d);
            else byKey.set(key, [d]);
        }
        // Newest first. createdAt is an ISO string, so a lexical compare is a
        // chronological one; a null timestamp sorts last rather than throwing
        // the order.
        return Array.from(byKey.entries()).map(
            ([key, list]) =>
                [
                    key,
                    list.slice().sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "")),
                ] as const,
        );
    }, [drafts]);

    const toggle = (key: string) =>
        setExpanded((cur) => {
            const next = new Set(cur);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    return (
        <>
            {groups.map(([key, list]) => {
                const open = expanded.has(key);
                const shown = open
                    ? list
                    : list.filter((d, i) => i === 0 || d.status === "published");
                const folded = list.length - shown.length;
                return (
                    <div key={key} className="space-y-2.5">
                        {shown.map((d) => (
                            <DraftRow
                                key={d.id}
                                draft={d}
                                onPublished={onPublished}
                                onMarkedByHand={onMarkedByHand}
                                showType={showType}
                                publishBlockedReason={publishBlockedReason}
                                publishUnsupported={publishUnsupported}
                            />
                        ))}
                        {(folded > 0 || open) && (
                            <button
                                onClick={() => toggle(key)}
                                className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800"
                            >
                                {open ? (
                                    <>
                                        <ChevronUpIcon className="h-3.5 w-3.5" />
                                        Hide earlier versions
                                    </>
                                ) : (
                                    <>
                                        <ChevronDownIcon className="h-3.5 w-3.5" />
                                        {folded} earlier {folded === 1 ? "version" : "versions"}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                );
            })}
        </>
    );
}

function PublicationRow({ publication, onReverted }: { publication: AeoPublication; onReverted: () => void }) {
    const snackbar = useSnackbar();
    const [reverting, setReverting] = useState(false);
    const failed = publication.status === "failed";
    const reverted = publication.status === "reverted";

    const doRevert = async () => {
        setReverting(true);
        const ok = await revertPublication(publication.id);
        setReverting(false);
        if (ok) {
            snackbar.showSuccess("Change rolled back");
            onReverted();
        } else {
            snackbar.showError("Could not roll back", "The previous value is no longer available.");
        }
    };

    return (
        <div className="flex items-start justify-between gap-3 py-2.5">
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    {failed ? (
                        <AlertTriangleIcon className="h-4 w-4 flex-shrink-0 text-rose-500" />
                    ) : (
                        <CheckCircle2Icon className={cn("h-4 w-4 flex-shrink-0", reverted ? "text-gray-400" : "text-emerald-500")} />
                    )}
                    <span className="truncate text-sm text-gray-900">{publication.target ?? "—"}</span>
                </div>
                <p className="mt-0.5 pl-6 text-xs text-gray-500">
                    {failed
                        ? publication.error || "Publish failed"
                        : reverted
                            ? `Rolled back ${whenLabel(publication.revertedAt)}`
                            : `Published ${whenLabel(publication.publishedAt)} · via ${publication.connector}`}
                </p>
            </div>
            {publication.reversible && (
                <Button size="sm" variant="ghost" className="flex-shrink-0" disabled={reverting} onClick={() => void doRevert()}>
                    {reverting ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" /> : <UndoIcon className="h-3.5 w-3.5" />}
                    Roll back
                </Button>
            )}
        </div>
    );
}

export function RemediationSection({
    businessId,
    issueKey,
    draftTypes,
    pageUrl,
    trackerTaskId,
    unansweredQuestions,
    publishBlockedReason,
    publishUnsupported,
}: {
    businessId: string;
    issueKey: string;
    /** What a draft can fix for this finding, as the backend declares it
     *  (#4529 §1). `[]` means none can, and this section says so instead of
     *  offering a generator that cannot help. Absent = older payload, fall
     *  back to the local map. */
    draftTypes?: string[];
    /** The page the fix applies to. Without one there is nothing to write. */
    pageUrl?: string;
    /** Scopes drafts to the tracker task that owns them (#3804 / #3817). */
    trackerTaskId?: string;
    /** From answerCoverage.gaps — turns "the site never answers this" into a
     *  brief for new content rather than an edit to existing content. */
    unansweredQuestions?: string[];
    /** See DraftRow — why publishing cannot work right now (#3400). */
    publishBlockedReason?: string;
    /** See DraftRow — threaded through unchanged (#3692). */
    publishUnsupported?: (draftType: string) => string | undefined;
}) {
    const snackbar = useSnackbar();
    const [drafts, setDrafts] = useState<AeoDraft[] | null>(null);
    const [publications, setPublications] = useState<AeoPublication[]>([]);
    const options = typesForIssue(issueKey, draftTypes);
    const [type, setType] = useState(options[0]?.key ?? "");
    const selected = options.find((t) => t.key === type) ?? options[0];
    const [generating, setGenerating] = useState(false);

    const load = useCallback(async () => {
        const [d, p] = await Promise.all([
            listDrafts(businessId, trackerTaskId),
            listPublications(businessId),
        ]);
        setDrafts(d);
        setPublications(p);
    }, [businessId, trackerTaskId]);

    useEffect(() => {
        let cancelled = false;
        setDrafts(null);
        void (async () => {
            const [d, p] = await Promise.all([
                listDrafts(businessId, trackerTaskId),
                listPublications(businessId),
            ]);
            if (cancelled) return;
            setDrafts(d);
            setPublications(p);
        })();
        return () => { cancelled = true; };
    }, [businessId, trackerTaskId]);

    const generate = async () => {
        if (!pageUrl) return;
        setGenerating(true);
        const draft = await createDraft(businessId, {
            page_url: pageUrl,
            issue_key: issueKey,
            draft_type: type,
            tracker_task_id: trackerTaskId,
            unanswered_questions: unansweredQuestions,
        });
        setGenerating(false);
        if (draft) {
            snackbar.showSuccess("Draft ready", "Review it before publishing.");
            void load();
        } else {
            snackbar.showError("Could not generate a draft", "Please try again.");
        }
    };

    // Only publications for the page this section is about — a drawer for one
    // issue showing every write ever made to the site is noise.
    const relevant = pageUrl ? publications.filter((p) => p.target === pageUrl) : publications;

    /**
     * The drafts that belong to THIS page and THIS issue.
     *
     * `listDrafts` returns every draft for the business — up to 200 of them —
     * and this section rendered the lot. Opening the homepage's site-audit tab
     * therefore listed drafts written for `/hello-world` and
     * `/general-dental-services` underneath it, which is what "multiple drafts"
     * turned out to be: not several versions of one thing, but every draft in
     * the workspace shown under whichever page you happened to be looking at.
     *
     * Publications on the line above were already filtered by page. Drafts were
     * not, and drafts are the half with a publish button on them — the same
     * wrong-publish path `IssuePagesRemediation` guards against, where one
     * page's copy can be pushed live from another page's context.
     *
     * Scoped on BOTH keys, matching that component's rule exactly: the issue,
     * because a `page_copy` draft written for thin content is not an answer to
     * missing structured data; and the page, normalised, because the same page
     * arrives spelled with and without its trailing slash.
     */
    const pageDrafts = useMemo(() => {
        if (drafts === null) return null;
        const wantPage = pageUrl ? draftPageKey(pageUrl) : null;
        return drafts.filter(
            (d) =>
                d.issueKey === issueKey &&
                (wantPage === null || draftPageKey(d.pageUrl) === wantPage),
        );
    }, [drafts, issueKey, pageUrl]);

    return (
        <section>
            <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
                <SparklesIcon className="h-3.5 w-3.5" />
                Let Oura fix it
            </h3>

            {options.length === 0 ? (
                /* The backend says no draft resolves this finding (#4529 §1) —
                   a redirect chain, a canonical, a missing robots.txt and a slow
                   page are configuration or infrastructure, and nothing a
                   generator writes changes any of them. Saying that is the
                   honest answer; the generator used to offer an FAQ block. */
                <p className="text-sm text-gray-500">
                    Nothing here can be written for you. This one is fixed in the site's
                    settings or its hosting, not in its words.
                </p>
            ) : !pageUrl ? (
                // No page means nothing to write to. Say so rather than showing a
                // button that cannot work.
                <p className="text-sm text-gray-500">
                    This one has no specific page attached, so there's nothing to generate against yet.
                </p>
            ) : (
                <>
                    <div className="flex flex-wrap items-end gap-2">
                        {/* Only shown when the issue really has more than one way to
                            fix it (thin content, orphan pages). For everything else
                            the list was one item, and a dropdown you cannot change
                            reads as a choice being withheld. Same rule as the
                            affected-pages table in IssuePagesRemediation. */}
                        {options.length > 1 && (
                            <div className="min-w-[200px] flex-1">
                                <span className="mb-1 block text-xs text-gray-500">What should we write?</span>
                                <Select value={type} onValueChange={setType}>
                                    <SelectTrigger className="w-full text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {options.map((t) => (
                                            <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <Button disabled={generating} onClick={() => void generate()}>
                            {generating ? (
                                <>
                                    <Loader2Icon className="h-4 w-4 animate-spin" />
                                    Writing…
                                </>
                            ) : (
                                <>
                                    <SparklesIcon className="h-4 w-4" />
                                    Generate a draft
                                </>
                            )}
                        </Button>
                    </div>
                    {/* The hint for the SELECTED type only — it used to be glued
                        onto every option label, which made a four-item list
                        unscannable. With no dropdown, the type name moves here so
                        it is still stated somewhere. */}
                    <p className="mt-1.5 text-xs text-gray-400">
                        {options.length > 1 ? selected.hint : `${selected.label}. ${selected.hint}`}{" "}
                        Nothing is published until you review the draft and say so.
                    </p>
                </>
            )}

            <div className="mt-4 space-y-2.5">
                {pageDrafts === null ? (
                    <div className="py-6 text-center">
                        <Loader2Icon className="mx-auto h-4 w-4 animate-spin text-gray-400" />
                    </div>
                ) : pageDrafts.length === 0 ? (
                    <p className="flex items-center gap-1.5 text-sm text-gray-400">
                        <FileTextIcon className="h-4 w-4" />
                        No drafts for this yet.
                    </p>
                ) : (
                    <DraftStack
                        drafts={pageDrafts}
                        onPublished={() => void load()}
                        showType={options.length > 1}
                        publishBlockedReason={publishBlockedReason}
                        publishUnsupported={publishUnsupported}
                    />
                )}
            </div>

            {relevant.length > 0 && (
                <div className="mt-5">
                    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                        What we've changed
                    </h4>
                    <div className="divide-y divide-gray-100">
                        {relevant.map((p) => (
                            <PublicationRow key={p.id} publication={p} onReverted={() => void load()} />
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}
